import type { ReportRowType } from '~/presentation/reports/common/type';

import { Box, Button, InlineStack, Text, BlockStack, Tooltip, Icon } from '@shopify/polaris';
import { EReportColumnKey } from '~/presentation/reports/common/type';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { ChartLineIcon, OrderIcon } from '@shopify/polaris-icons';
import { ModalType, useGModalContext } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { dayjsTz } from '~/utils/dayjs';

export interface Order {
  id: string;
  createdAt: string;
}

interface OrderItemProps {
  order: ReportRowType;
}

export const OrderItem = ({ order }: OrderItemProps) => {
  const { t } = useTranslation();

  const { onOpenModal } = useGModalContext<ModalType.ORDER_JOURNEY>();
  const { handleOpenPageShopifyInNewTab } = useLocationHandlers();

  const handleViewOrderJourney = (orderId: string) => {
    onOpenModal(ModalType.ORDER_JOURNEY, { id: orderId, orderId, gxId });
  };

  const handleViewOrderDetails = (orderId: string) => {
    handleOpenPageShopifyInNewTab(`orders/${orderId}`);
  };

  const formatDate = (date: string) => {
    return dayjsTz(date).format('MMMM D, YYYY [at] h:mm a');
  };

  const orderId = `${order[EReportColumnKey.ORDER_ID]}`;
  const date = `${order[EReportColumnKey.ORDER_DATE]}`;
  const rawGxId = order[EReportColumnKey.GEMX_ID];
  const gxId = rawGxId ? `${rawGxId}` : '';

  if (!orderId) return null;

  return (
    <Box padding="300" borderBlockEndWidth="025" borderColor="border">
      <InlineStack align="space-between" blockAlign="center" gap="200">
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            {`ID: ${orderId}`}
          </Text>
          <Text as="p" variant="bodyXs" tone="subdued">
            {formatDate(date)}
          </Text>
          {gxId && (
            <InlineStack gap="100" blockAlign="center">
              <Text as="span" variant="bodyXs">
                {t('GemX ID:')}
              </Text>
              <Box
                background="bg-surface-emphasis-active"
                paddingBlock="050"
                paddingInline="150"
                borderRadius="200"
              >
                <Text as="span" variant="bodySm">
                  <span style={{ color: 'var(--p-color-text-emphasis-active)' }}>{gxId}</span>
                </Text>
              </Box>
            </InlineStack>
          )}
        </BlockStack>
        <InlineStack gap="100">
          <Tooltip content={t('View order details')}>
            <Button
              icon={<Icon source={OrderIcon} tone="critical" />}
              variant="tertiary"
              size="slim"
              onClick={() => handleViewOrderDetails(orderId)}
            />
          </Tooltip>
          <Tooltip content={t('View order journey')}>
            <Button
              icon={<Icon source={ChartLineIcon} tone="primary" />}
              variant="tertiary"
              size="slim"
              onClick={() => handleViewOrderJourney(orderId)}
            />
          </Tooltip>
        </InlineStack>
      </InlineStack>
    </Box>
  );
};
